import { useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import {
  ArrowRight,
  BookOpen,
  Check,
  ClipboardCopy,
  KeyRound,
  LifeBuoy,
  MessagesSquare,
  PartyPopper,
  PlugZap,
  Webhook,
  Workflow,
  Zap,
} from "lucide-react";
import { useAppServices } from "../../hook/services";
import { cx, toEmbedUrl } from "../../utils";
import CopyButtons from "../../components/ui/CopyButtons";
import EmptyState from "../../components/ui/EmptyState";
import Spinner from "../../components/ui/Spinner";

// featuresInclude is authored as plain strings, but older rows and the admin's
// select have carried { name } / { label } shapes.
const featureName = (f) => (typeof f === "string" ? f : f?.name || f?.label || "");

// What each feature means for the customer, in their words.
const FEATURE_INFO = {
  "conversation provider": { icon: MessagesSquare, text: "Send and receive messages in your CRM conversations" },
  actions: { icon: Workflow, text: "Steps you can add to your workflows" },
  triggers: { icon: Zap, text: "Start workflows when something happens" },
};

/**
 * Setup, as a stepper across the top — every step with its own action, the
 * next one highlighted — then the written guide beside the things the guide
 * asks for: the values to copy and where to get help.
 */

const GetStartedPanel = ({
  app,
  install,
  progress,
  onOpenCredentials,
  onOpenUsage,
  onOpenSupport,
  appId,
  locationId,
  companyId,
  onWebhookConfirmed,
}) => {
  const credentialsComplete = progress?.ready ?? false;
  const embedUrl = useMemo(() => toEmbedUrl(app?.getStarted?.video || ""), [app]);

  // Authored in the admin and stored on the app row, but it still reaches the
  // browser as raw HTML — sanitize before injecting.
  const safeContent = useMemo(() => {
    const raw = app?.getStarted?.content || app?.description || "";
    return raw ? DOMPurify.sanitize(raw) : "";
  }, [app]);

  const fieldCount = app?.formFields?.length || 0;
  const hasCopyButtons = (app?.copy_buttons || []).some((b) => b?.title && b?.copy_url);

  // Only a conversation provider has an inbound webhook to register.
  const needsWebhook = useMemo(
    () => (app?.featuresInclude || []).some((f) => featureName(f).toLowerCase() === "conversation provider"),
    [app],
  );

  const autoVerified = Boolean(install?.webhookVerifiedAt);
  const [webhookChecked, setWebhookChecked] = useState(Boolean(install?.webhookConfirmedAt));
  const [savingWebhook, setSavingWebhook] = useState(false);

  useEffect(() => {
    setWebhookChecked(Boolean(install?.webhookConfirmedAt));
  }, [install?.webhookConfirmedAt]);

  const { installs } = useAppServices();

  const onWebhookChange = async (next) => {
    // Tick immediately; reverted below if the save fails.
    setWebhookChecked(next);
    setSavingWebhook(true);
    const { error } = await installs.SetWebhookConfirmed({
      appId,
      ...(locationId && { locationId }),
      ...(companyId && { companyId }),
      confirmed: next,
    });
    if (error) setWebhookChecked(!next);
    else onWebhookConfirmed?.(next);
    setSavingWebhook(false);
  };

  const webhookDone = autoVerified || webhookChecked;
  const liveReady = credentialsComplete && (!needsWebhook || webhookDone);

  const steps = [
    {
      id: "installed",
      icon: PlugZap,
      title: "App installed",
      text: install?.locationName || install?.companyName ? `On ${install.locationName || install.companyName}` : "This account has the app",
      done: true,
    },
    {
      id: "credentials",
      icon: KeyRound,
      title: "Add your credentials",
      text:
        fieldCount === 0
          ? "None needed for this app"
          : credentialsComplete
            ? "All values saved"
            : `${fieldCount} ${fieldCount === 1 ? "value" : "values"} from your provider`,
      done: credentialsComplete,
      action: !credentialsComplete && fieldCount > 0 && (
        <button
          type="button"
          onClick={onOpenCredentials}
          className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-gradient-to-br from-brand-500 to-violet-600 px-3 text-[12.5px] font-semibold text-white shadow-brand-glow transition-all hover:brightness-110"
        >
          Add credentials <ArrowRight size={13} />
        </button>
      ),
    },
    ...(needsWebhook
      ? [
          {
            id: "webhook",
            icon: Webhook,
            title: "Register the webhook",
            text: autoVerified
              ? "Confirmed — a reply has come through"
              : "So replies reach your conversations",
            done: webhookDone,
            action: !autoVerified && (
              <label
                className={cx(
                  "inline-flex cursor-pointer select-none items-center gap-2 rounded-lg border px-2.5 py-1.5 text-[12.5px] font-medium transition-colors",
                  webhookChecked
                    ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                    : "border-ink-200 bg-white text-ink-700 hover:border-ink-300 hover:bg-ink-50",
                )}
              >
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5 cursor-pointer rounded border-ink-300 text-brand-600 focus:ring-brand-500"
                  checked={webhookChecked}
                  disabled={savingWebhook}
                  onChange={(e) => onWebhookChange(e.target.checked)}
                />
                I've added it
                {savingWebhook && <Spinner size={12} />}
              </label>
            ),
          },
        ]
      : []),
  ];

  const doneCount = steps.filter((s) => s.done).length;
  const nextId = steps.find((s) => !s.done)?.id;
  const pct = Math.round((doneCount / steps.length) * 100);

  return (
    <div className="space-y-5">
      {/* ---------------- Setup stepper ---------------- */}
      <section className="overflow-hidden rounded-2xl border border-ink-200/70 bg-white shadow-card">
        <div className="flex flex-wrap items-end justify-between gap-3 px-6 pt-5">
          <div>
            <h2 className="text-[17px] font-semibold tracking-tight text-ink-900">
              {liveReady ? "You're all set" : `Set up ${app.appName}`}
            </h2>
            <p className="mt-0.5 text-[13px] text-ink-500">
              {liveReady
                ? "Everything's connected. Here's how to start using it."
                : `${doneCount} of ${steps.length} steps done — a few minutes and you're live.`}
            </p>
          </div>
          <span className="text-[12px] font-semibold tabular-nums text-ink-500">{pct}%</span>
        </div>
        <div className="mx-6 mt-3 h-1.5 overflow-hidden rounded-full bg-ink-100">
          <div
            className={cx("h-full rounded-full transition-[width] duration-700", liveReady ? "bg-emerald-500" : "bg-gradient-to-r from-brand-500 to-violet-500")}
            style={{ width: `${pct}%` }}
          />
        </div>

        <ol className={cx("grid gap-3 p-4 sm:p-5", steps.length === 3 ? "md:grid-cols-4" : "md:grid-cols-3")}>
          {steps.map((s, i) => {
            const Icon = s.icon;
            const isNext = s.id === nextId;
            return (
              <li
                key={s.id}
                className={cx(
                  "flex flex-col rounded-xl border p-4 transition-all",
                  s.done
                    ? "border-emerald-200/70 bg-emerald-50/40"
                    : isNext
                      ? "border-brand-300 bg-brand-50/40 shadow-glow"
                      : "border-ink-200/80 bg-white",
                )}
              >
                <div className="flex items-center justify-between">
                  <span
                    className={cx(
                      "grid h-9 w-9 place-items-center rounded-xl",
                      s.done
                        ? "bg-emerald-500 text-white"
                        : isNext
                          ? "bg-gradient-to-br from-brand-500 to-violet-600 text-white shadow-brand-glow"
                          : "bg-ink-100 text-ink-400",
                    )}
                  >
                    {s.done ? <Check size={17} strokeWidth={2.75} /> : <Icon size={17} />}
                  </span>
                  <span className={cx("text-[11px] font-semibold uppercase tracking-wider", s.done ? "text-emerald-600" : isNext ? "text-brand-600" : "text-ink-400")}>
                    {s.done ? "Done" : isNext ? "Next" : `Step ${i + 1}`}
                  </span>
                </div>
                <p className="mt-3 text-[14px] font-semibold text-ink-900">{s.title}</p>
                <p className="mt-0.5 text-[12.5px] leading-relaxed text-ink-500">{s.text}</p>
                {s.action && <div className="mt-3">{s.action}</div>}
              </li>
            );
          })}

          {/* The goal, last in the row */}
          <li
            className={cx(
              "flex flex-col rounded-xl border p-4",
              liveReady ? "border-emerald-300 bg-gradient-to-br from-emerald-50 to-teal-50" : "border-dashed border-ink-200 bg-ink-50/50",
            )}
          >
            <span className={cx("grid h-9 w-9 place-items-center rounded-xl", liveReady ? "bg-emerald-500 text-white" : "bg-ink-100 text-ink-300")}>
              <PartyPopper size={17} />
            </span>
            <p className={cx("mt-3 text-[14px] font-semibold", liveReady ? "text-emerald-800" : "text-ink-400")}>You're live</p>
            <p className={cx("mt-0.5 text-[12.5px] leading-relaxed", liveReady ? "text-emerald-700" : "text-ink-400")}>
              {liveReady ? "Messages can flow both ways." : "Finish the steps to get here."}
            </p>
            {liveReady && onOpenUsage && (
              <button
                type="button"
                onClick={onOpenUsage}
                className="mt-3 inline-flex h-8 w-fit items-center gap-1.5 rounded-lg bg-emerald-600 px-3 text-[12.5px] font-semibold text-white transition-colors hover:bg-emerald-700"
              >
                How to use it <ArrowRight size={13} />
              </button>
            )}
          </li>
        </ol>
      </section>

      {/* ---------------- Guide + sidebar ---------------- */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_20rem] 2xl:grid-cols-[minmax(0,1fr)_24rem]">
        <div className="min-w-0 space-y-5">
          {/* No placeholder when there is no video — an empty slot advertising a
              missing feature is worse than simply not having the section. */}
          {embedUrl && (
            <div className="overflow-hidden rounded-2xl border border-ink-200/70 bg-ink-900 shadow-card">
              <div className="aspect-video">
                <iframe
                  src={embedUrl}
                  title={`${app.appName} walkthrough`}
                  className="h-full w-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                  allowFullScreen
                />
              </div>
            </div>
          )}

          <article className="overflow-hidden rounded-2xl border border-ink-200/70 bg-white shadow-card">
            <div className="flex items-center gap-2.5 border-b border-ink-150 px-6 py-3.5">
              <BookOpen size={15} className="text-brand-600" />
              <h3 className="text-[13px] font-semibold text-ink-800">Setup guide</h3>
            </div>
            {safeContent ? (
              // Sanitized above with DOMPurify.
              <div className="prose-app max-w-4xl px-6 py-6 sm:px-8" dangerouslySetInnerHTML={{ __html: safeContent }} />
            ) : (
              <EmptyState
                tone="amber"
                icon={<BookOpen size={24} />}
                title="Nothing written up yet"
                description="Setup instructions for this app haven't been added."
              />
            )}
          </article>
        </div>

        <aside className="space-y-5 lg:sticky lg:top-20 lg:self-start">
          {hasCopyButtons && (
            <section className="overflow-hidden rounded-2xl border border-brand-200/70 bg-white shadow-card">
              <div className="flex items-start gap-3 border-b border-brand-100 bg-brand-subtle px-5 py-4">
                <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-gradient-to-br from-brand-500 to-violet-600 text-white">
                  <ClipboardCopy size={15} />
                </span>
                <div>
                  <h3 className="text-[13.5px] font-semibold text-ink-900">Copy &amp; paste</h3>
                  <p className="mt-0.5 text-[12px] leading-relaxed text-ink-600">Paste these into your provider account to finish setup.</p>
                </div>
              </div>
              <div className="p-4">
                <CopyButtons buttons={app.copy_buttons} context={{ appId, locationId, companyId }} />
              </div>
            </section>
          )}

          {(app.featuresInclude?.length > 0 || app.actions?.length > 0 || app.triggers?.length > 0) && (
            <section className="rounded-2xl border border-ink-200/70 bg-white p-5 shadow-card">
              <h3 className="text-[13.5px] font-semibold text-ink-900">What's included</h3>
              <ul className="mt-3 space-y-3">
                {(app.featuresInclude || []).map((f, i) => {
                  const name = featureName(f);
                  const info = FEATURE_INFO[name.toLowerCase()] || { icon: Zap, text: "" };
                  const Icon = info.icon;
                  return (
                    <li key={i} className="flex gap-3">
                      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-brand-50 text-brand-600 ring-1 ring-brand-200/60">
                        <Icon size={15} />
                      </span>
                      <div className="min-w-0">
                        <p className="text-[13px] font-medium text-ink-800">{name}</p>
                        {info.text && <p className="text-[12px] leading-relaxed text-ink-500">{info.text}</p>}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          <section className="rounded-2xl border border-ink-200/70 bg-white p-5 shadow-card">
            <div className="flex items-center gap-3">
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-brand-500 to-violet-600 text-white shadow-brand-glow">
                <LifeBuoy size={16} />
              </span>
              <div>
                <h3 className="text-[13.5px] font-semibold text-ink-900">Stuck on a step?</h3>
                <p className="text-[12px] text-ink-500">The team that builds the app will help.</p>
              </div>
            </div>
            <button
              type="button"
              onClick={onOpenSupport}
              className="mt-4 inline-flex h-9 w-full items-center justify-center gap-1.5 rounded-xl border border-ink-200 bg-white text-[13px] font-medium text-ink-700 shadow-sm transition-colors hover:bg-ink-50"
            >
              Open a support ticket <ArrowRight size={13} />
            </button>
          </section>
        </aside>
      </div>
    </div>
  );
};

export default GetStartedPanel;
